import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type Theme,
  styled,
} from '@mui/material';

import SortIcon from '@mui/icons-material/Sort';

const PlaceHolderContainer = styled('div')(({ theme }: { theme: Theme }) => ({
  color: theme.palette.common.black,
  background: theme.palette.common.white,
  marginLeft: '5px 0px',
  textAlign: 'center',
  '& span': {
    position: 'relative',
    bottom: '5px',
  },
}));

export enum SearchSortByEnum {
  RELEVANCE = 'Relevance',
  NAME_ASCENDING = 'NameAsc',
  NAME_DESCENDING = 'NameDesc',
  SCORE_ASCENDING = 'AccreditationScoreAsc',
  SCORE_DESCENDING = 'AccreditationScoreDesc',
}

export type SortByProps = {
  handleSelectedChange: (selectedOption: string) => void;
  isRelevanceDisabled: boolean;
  label: string;
  options?: SortByOption[];
  value?: string;
  open?: boolean;
};

export type SortByOption = {
  enum: SearchSortByEnum;
  label: string;
};

/**
 * Default options for the SortBy component from [MicroLibrarySearchUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibrarySearchUI)
 */
export const sortByDefaultOptions: SortByOption[] = [
  {
    enum: SearchSortByEnum.RELEVANCE,
    label: 'Relevance',
  },
  {
    enum: SearchSortByEnum.SCORE_DESCENDING,
    label: 'Score (high-low)',
  },
  {
    enum: SearchSortByEnum.SCORE_ASCENDING,
    label: 'Score (low-high)',
  },
  {
    enum: SearchSortByEnum.NAME_ASCENDING,
    label: 'Name (A-Z)',
  },
  {
    enum: SearchSortByEnum.NAME_DESCENDING,
    label: 'Name (Z-A)',
  },
];

/**
 * SortBy component from [MicroLibrarySearchUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibrarySearchUI)
 * @param props - SortByProps
 * @returns SortBy component
 */

export const SortBy = (props: SortByProps) => {
  const {
    value,
    handleSelectedChange,
    options,
    isRelevanceDisabled,
    open,
    label,
  } = props;

  const sortByOptions = options ?? sortByDefaultOptions;

  const handleChange = (event: SelectChangeEvent) => {
    handleSelectedChange(event.target.value as SearchSortByEnum);
  };

  return (
    <>
      {sortByOptions && (
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel id="sort-by-select-input-label">
            <PlaceHolderContainer>
              {value === undefined && (
                <SortIcon fontSize="small" sx={{ mr: 1 }} />
              )}
              <span>{label}</span>
            </PlaceHolderContainer>
          </InputLabel>
          <Select
            labelId="sort-by-select-input-label"
            data-testid="sort-by-select"
            onChange={handleChange}
            autoWidth
            label={label}
            value={value}
            open={open}
          >
            {sortByOptions.map((option) => {
              const isItemDisabled =
                option.enum === SearchSortByEnum.RELEVANCE &&
                isRelevanceDisabled;
              return (
                <MenuItem
                  key={option.enum}
                  disabled={isItemDisabled}
                  value={option.enum}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
    </>
  );
};
